import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowDownToLine, ArrowUp, ArrowUpToLine } from 'lucide-react';
import { byPosition, type BoardSnapshot } from '@kaba/shared';
import { neighborsAt, useMoveCard } from '../../lib/board';
import { toast } from '../../lib/toast';
import { Button, cx, Sheet } from '../ui/ui';

/**
 * „Verschieben nach …“: vollständiger Bedienweg ohne Ziehen (WCAG 2.2, 2.5.7).
 * Erreicht jede Spalte und jede Position.
 */
export function MoveDialog({
  snapshot,
  cardId,
  onClose,
}: {
  snapshot: BoardSnapshot;
  cardId: string | null;
  onClose: () => void;
}) {
  const live = snapshot.cards.find((c) => c.id === cardId);
  // Verschwindet die Karte während des Dialogs (archiviert, gelöscht), sauber schließen statt den Dialog
  // samt Fokus einfach abzubauen (F08)
  const last = useRef(live);
  if (live) last.current = live;
  const card = live ?? (cardId && last.current?.id === cardId ? last.current : undefined);
  const gone = !!cardId && !live && !!card;
  useEffect(() => {
    if (!gone) return;
    toast.info('Die Karte ist nicht mehr auf dem Board (z. B. archiviert).');
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gone]);
  const columns = useMemo(() => [...snapshot.columns].sort(byPosition), [snapshot.columns]);
  const [columnId, setColumnId] = useState(card?.columnId ?? '');
  const [position, setPosition] = useState(0);
  const move = useMoveCard(snapshot.board.id);
  /** Zielspalte nach erfolgreichem Verschieben (für die Fokusrückkehr) */
  const moved = useRef<string | null>(null);
  useEffect(() => {
    if (cardId) moved.current = null;
  }, [cardId]);

  const others = useMemo(
    () => snapshot.cards.filter((c) => c.columnId === columnId && c.id !== cardId).sort(byPosition),
    [snapshot.cards, columnId, cardId],
  );

  useEffect(() => {
    if (!card) return;
    setColumnId(card.columnId);
    const same = snapshot.cards.filter((c) => c.columnId === card.columnId).sort(byPosition);
    setPosition(Math.max(0, same.findIndex((c) => c.id === card.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId]);

  if (!card) return null;
  const currentIndex = snapshot.cards
    .filter((c) => c.columnId === card.columnId)
    .sort(byPosition)
    .findIndex((c) => c.id === card.id);
  const columnCount = snapshot.cards.filter((c) => c.columnId === card.columnId).length;

  const doMove = (colId: string, index: number) => {
    moved.current = colId;
    const n = neighborsAt(snapshot.cards, colId, card.id, index);
    move.mutate({ cardId: card.id, columnId: colId, beforeId: n.beforeId, afterId: n.afterId });
    onClose();
  };

  return (
    <Sheet
      open={!!cardId}
      onOpenChange={(o) => !o && onClose()}
      title="Verschieben nach …"
      side="center"
      onCloseAutoFocus={(e) => {
        // Fokus nicht im Nichts lassen (F08): zurück dorthin, woher der Dialog kam
        e.preventDefault();
        const id = card.id;
        const target = moved.current;
        const from = card.columnId;
        requestAnimationFrame(() => requestAnimationFrame(() => returnFocus(id, target, from)));
      }}
    >
      <div className="flex flex-col gap-4 p-4">
        <p className="truncate text-sm text-[var(--ink-soft)]">„{card.title}“</p>

        <div className="grid grid-cols-2 gap-2">
          <Button disabled={currentIndex <= 0} onClick={() => doMove(card.columnId, 0)}>
            <ArrowUpToLine size={16} /> An den Anfang
          </Button>
          <Button disabled={currentIndex >= columnCount - 1} onClick={() => doMove(card.columnId, columnCount - 1)}>
            <ArrowDownToLine size={16} /> Ans Ende
          </Button>
          <Button disabled={currentIndex <= 0} onClick={() => doMove(card.columnId, currentIndex - 1)}>
            <ArrowUp size={16} /> Eins hoch
          </Button>
          <Button disabled={currentIndex >= columnCount - 1} onClick={() => doMove(card.columnId, currentIndex + 1)}>
            <ArrowDown size={16} /> Eins runter
          </Button>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">Spalte</legend>
          <div className="flex flex-wrap gap-2">
            {columns.map((col) => (
              <button
                key={col.id}
                type="button"
                aria-pressed={col.id === columnId}
                onClick={() => {
                  setColumnId(col.id);
                  setPosition(col.id === card.columnId ? Math.max(0, currentIndex) : 0);
                }}
                className={cx(
                  'min-h-11 rounded-lg border px-3 text-sm',
                  col.id === columnId ? 'border-accent bg-accent-soft font-semibold' : 'border-[var(--line)]',
                )}
              >
                {col.title}
                {col.id === card.columnId && <span className="text-[var(--ink-soft)]"> (aktuell)</span>}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Position</span>
          <select
            value={position}
            onChange={(e) => setPosition(Number(e.target.value))}
            className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base sm:text-sm"
          >
            <option value={0}>1 – ganz oben</option>
            {others.map((o, i) => (
              <option key={o.id} value={i + 1}>
                {i + 2} – nach „{o.title.slice(0, 40)}“
              </option>
            ))}
          </select>
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={() => doMove(columnId, position)}>
            Verschieben
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

/**
 * Fokus nach dem Verschiebedialog (F08):
 * - aus den Kartendetails geöffnet → deren Menüknopf (der Dialog bleibt die Arbeitsfläche),
 * - abgebrochen → Menüknopf der Karte, sonst die Karte,
 * - verschoben → die Karte an ihrem neuen Ort; ist sie nicht sichtbar (gefiltert), die erste Karte der Zielspalte.
 */
function returnFocus(cardId: string, movedTo: string | null, fromColumn: string) {
  const focus = (el: Element | null | undefined) => {
    if (el instanceof HTMLElement && el.isConnected) {
      el.focus();
      return document.activeElement === el;
    }
    return false;
  };
  const detail = document.querySelector('[role="dialog"] button[aria-label="Weitere Aktionen"]');
  if (detail && focus(detail)) return;
  const tile = document.querySelector(`[data-card-id="${CSS.escape(cardId)}"]`);
  if (!movedTo && tile && focus(tile.querySelector('button[aria-label^="Aktionen für"]'))) return;
  if (focus(tile)) return;
  // Karte nicht (mehr) sichtbar: erste Karte bzw. Menü der Ziel- oder Ausgangsspalte
  for (const colId of [movedTo, fromColumn]) {
    if (!colId) continue;
    const col = document.getElementById(`col-${colId}`);
    if (focus(col?.querySelector('[data-card-id]'))) return;
    if (focus(col?.querySelector('button'))) return;
  }
  // Liste: erste Karte überhaupt
  focus(document.querySelector('[data-card-id]'));
}
